import React, { useState } from 'react';
import { BarChart3, Bot, Shield, Target, Zap, TrendingUp, Star, Activity, ArrowLeft, ChevronRight } from 'lucide-react'

interface FeaturesProps {
  onLaunchPlatform?: () => void;
  onNavigateToPage?: (page: string) => void;
}

export default function FeaturesPage({ onLaunchPlatform, onNavigateToPage }: FeaturesProps) {
  const [currentView, setCurrentView] = useState<string>('main');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const features = [
    {
      id: 'advanced-dex',
      title: 'Advanced DEX Trading',
      subtitle: 'Professional Trading Suite',
      icon: <BarChart3 className="w-5 h-5 text-[#24c35b]" />,
      details: {
        title: 'Advanced DEX Trading',
        subtitle: 'Professional-Grade Trading Suite for Solana',
        description: 'Experience institutional-level trading capabilities with our comprehensive DEX interface, designed for both novice and professional traders.',
        capabilities: [
          {
            category: 'Order Types & Execution',
            features: [
              'Limit Orders - Set precise entry and exit points for optimal trading',
              'Market Orders - Instant execution at current market prices',
              'Stop-Loss Orders - Automated risk management and downside protection',
              'Take-Profit Orders - Secure profits at predetermined levels',
              'DCA (Dollar Cost Averaging) - Systematic position building strategies',
              'OCO (One-Cancels-Other) - Advanced conditional order management'
            ]
          },
          {
            category: 'Advanced Features',
            features: [
              'Grid Trading - Automated buy/sell orders across price ranges',
              'Portfolio Rebalancing - Maintain desired asset allocation automatically',
              'Multi-Wallet Support - Manage multiple trading accounts seamlessly',
              'Cross-Platform Sync - Access your trades across all devices',
              'Real-Time P&L Tracking - Monitor performance with detailed analytics',
              'Tax Reporting Integration - Simplified compliance and record keeping'
            ]
          },
          {
            category: 'Professional Analytics',
            features: [
              'Advanced Charting - TradingView integration with 100+ indicators',
              'Technical Analysis Tools - Support/resistance, Fibonacci, patterns',
              'Volume Analysis - Track whale movements and market sentiment',
              'Price Alerts - Customizable notifications for price movements',
              'Historical Data - Access to comprehensive market history',
              'Performance Metrics - ROI, Sharpe ratio, and risk assessment'
            ]
          }
        ]
      }
    },
    {
      id: 'sniper-bot',
      title: 'Lightning Sniper Bot',
      subtitle: 'MEV-Protected Execution',
      icon: <Bot className="w-5 h-5 text-[#24c35b]" />,
      details: {
        title: 'Lightning Sniper Bot',
        subtitle: 'MEV-Protected High-Speed Token Sniping',
        description: 'Gain the competitive edge with our lightning-fast sniper bot, featuring MEV protection and advanced filtering for optimal token launch entries.',
        capabilities: [
          {
            category: 'Speed & Performance',
            features: [
              'Sub-Millisecond Execution - Fastest transaction processing on Solana',
              'Private Node Access - Direct blockchain connection for minimal latency',
              'Parallel Processing - Multiple simultaneous transaction attempts',
              'Auto-Retry Logic - Persistent execution until successful completion',
              'Priority Fee Optimization - Smart gas fee calculation for faster inclusion',
              'Network Congestion Handling - Adaptive strategies for high-traffic periods'
            ]
          },
          {
            category: 'MEV Protection',
            features: [
              'Front-Running Prevention - Advanced protection against MEV attacks',
              'Sandwich Attack Defense - Sophisticated detection and mitigation',
              'Private Mempool Access - Transactions hidden from public visibility',
              'Jito Integration - Enhanced MEV protection through Jito bundles',
              'Slippage Protection - Dynamic slippage adjustment for optimal execution',
              'Transaction Simulation - Pre-execution validation and optimization'
            ]
          },
          {
            category: 'Smart Filtering & Analysis',
            features: [
              'Token Safety Scanner - Real-time contract analysis and risk assessment',
              'Liquidity Verification - Ensure sufficient market depth before entry',
              'Honeypot Detection - Advanced algorithms to identify malicious contracts',
              'Rug Pull Protection - Early warning system for exit scam detection',
              'Social Sentiment Analysis - Community mood and sentiment tracking',
              'Developer Background Check - Team verification and history analysis'
            ]
          }
        ]
      }
    },
    {
      id: 'portfolio-manager',
      title: 'AI Portfolio Manager',
      subtitle: 'Intelligent Analytics',
      icon: <TrendingUp className="w-5 h-5 text-[#24c35b]" />,
      details: {
        title: 'AI Portfolio Manager',
        subtitle: 'Intelligent Analytics & Automated Management',
        description: 'Harness the power of artificial intelligence to optimize your portfolio performance with automated rebalancing, risk management, and predictive analytics.',
        capabilities: [
          {
            category: 'AI-Powered Insights',
            features: [
              'Predictive Analytics - ML models forecasting price movements and trends',
              'Pattern Recognition - Identify recurring market patterns and opportunities',
              'Sentiment Analysis - Social media and news sentiment impact assessment',
              'Correlation Analysis - Asset relationship mapping for diversification',
              'Risk Scoring - Dynamic risk assessment for all portfolio positions',
              'Alpha Generation - AI-identified opportunities for outperformance'
            ]
          },
          {
            category: 'Automated Management',
            features: [
              'Dynamic Rebalancing - Maintain optimal portfolio allocation automatically',
              'Risk-Adjusted Position Sizing - Kelly Criterion and modern portfolio theory',
              'Stop-Loss Automation - Intelligent exit strategies based on volatility',
              'Profit Taking - Systematic profit realization with trend analysis',
              'Tax-Loss Harvesting - Optimize tax efficiency through strategic selling',
              'Cash Management - Optimal cash allocation and yield optimization'
            ]
          },
          {
            category: 'Performance Analytics',
            features: [
              'Real-Time Performance Tracking - Live P&L and portfolio metrics',
              'Benchmark Comparison - Performance vs SOL, BTC, and custom indices',
              'Attribution Analysis - Understand sources of portfolio returns',
              'Drawdown Analysis - Maximum drawdown and recovery time metrics',
              'Volatility Monitoring - Track portfolio risk and stability metrics',
              'Custom Reporting - Personalized performance reports and insights'
            ]
          }
        ]
      }
    },
    {
      id: 'security-suite',
      title: 'Security & Risk Management',
      subtitle: 'Enterprise Protection',
      icon: <Shield className="w-5 h-5 text-[#24c35b]" />,
      details: {
        title: 'Security & Risk Management',
        subtitle: 'Enterprise-Grade Protection & Risk Control',
        description: 'Comprehensive security framework protecting your assets with multi-layered defense systems, risk management tools, and continuous monitoring.',
        capabilities: [
          {
            category: 'Asset Protection',
            features: [
              'Multi-Signature Wallets - Require multiple approvals for large transactions',
              'Hardware Wallet Integration - Ledger and Trezor support for cold storage',
              'Time-Locked Transactions - Delayed execution for large trades',
              'Whitelisting System - Restrict interactions to approved addresses only',
              'Emergency Stop - Immediate halt of all trading activities if needed',
              'Insurance Coverage - Protection against smart contract vulnerabilities'
            ]
          },
          {
            category: 'Real-Time Monitoring',
            features: [
              'Anomaly Detection - AI-powered identification of unusual activity',
              'Transaction Monitoring - Real-time analysis of all blockchain interactions',
              'Wallet Health Checks - Continuous security assessment of connected wallets',
              'Threat Intelligence - Integration with security databases and feeds',
              'Compliance Monitoring - Ensure adherence to regulatory requirements',
              '24/7 Security Operations - Round-the-clock security team monitoring'
            ]
          },
          {
            category: 'Risk Management',
            features: [
              'Position Sizing Limits - Automatic enforcement of maximum position sizes',
              'Concentration Risk Controls - Prevent over-exposure to single assets',
              'Volatility-Based Limits - Dynamic risk limits based on market conditions',
              'Correlation Monitoring - Track and limit correlated position exposure',
              'Stress Testing - Portfolio performance under extreme market scenarios',
              'Risk Reporting - Comprehensive risk metrics and regular assessments'
            ]
          }
        ]
      }
    }
  ];

  const handleFeatureClick = (featureId: string) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView(featureId);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackClick = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('main');
      setIsTransitioning(false);
    }, 300);
  };

  const getCurrentFeature = () => features.find(f => f.id === currentView);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Background Hero Image - Fixed to bottom */}
      <div className="fixed bottom-0 right-0 w-full h-full pointer-events-none z-1">
        <div className="relative h-full flex items-end justify-end">
          <div className="relative">
            <img 
              src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/1891.png"
              alt="EKKO Features"
              className="h-full w-auto object-contain object-bottom"
              style={{ 
                height: '100vh',
                maxWidth: '60vw'
              }}
            />
            {/* Left fade gradient */}
            <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent"></div>
            {/* Right fade gradient */}
            <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
            </div>
            
        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute top-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute bottom-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
      </div>
            </div>
        <div className="absolute bottom-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
            </div>
                      </div>
          
      {/* Background Text - CENTERED AND STRAIGHT */}
      <div className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
        <div className="text-gray-200 font-black select-none text-center space-y-8">
          <div className="text-[12vw] opacity-20 leading-none">FEATURES</div>
          <div className="text-[10vw] opacity-15 leading-none">POWER</div>
          <div className="text-[8vw] opacity-10 leading-none">TOOLS</div>
                      </div>
                    </div>

      {/* Main Content - Transitioning Views */}
      <section className="relative z-10 min-h-screen flex items-center py-20">
        <div className="container mx-auto px-4">
          
          {/* Main Overview */}
          {currentView === 'main' && (
            <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <div className="max-w-2xl mx-auto lg:mx-0 lg:max-w-3xl">
                {/* Content */}
                <div className="text-center lg:text-left space-y-8">
                  {/* Title */}
                  <div className="space-y-6">
                    <h1 className="text-6xl lg:text-7xl font-black leading-tight">
                      <span className="bg-gradient-to-r from-[#24c35b] via-[#1eaa4e] to-[#24c35b] bg-clip-text text-transparent">
                        FEATURES
                      </span>
                    </h1>
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                      Advanced Trading
                      <br />
                      <span className="text-[#24c35b]">Made Simple</span>
                    </h2>
                      </div>

                  {/* Description */}
                  <p className="text-xl text-gray-600 leading-relaxed">
                    Discover powerful tools and capabilities designed to give you the competitive edge in Solana trading.
                  </p>
                  
                  {/* Clickable Feature Cards */}
                  <div className="grid grid-cols-1 gap-4 max-w-2xl mx-auto lg:mx-0">
                    {features.map((feature) => (
                      <button
                        key={feature.id}
                        onClick={() => handleFeatureClick(feature.id)}
                        className="w-full glassmorphism p-4 rounded-xl border border-white/20 flex items-center gap-4 hover:bg-gray-50 transition-all duration-300 group"
                      >
                        <div className="w-10 h-10 bg-[#24c35b]/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          {feature.icon}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-gray-900">{feature.title}</div>
                          <div className="text-sm text-gray-600">{feature.subtitle}</div>
                    </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                      </button>
                  ))}
                </div>
                
                  {/* Action Button */}
                  <div className="max-w-lg mx-auto lg:mx-0">
                    <button 
                      onClick={onLaunchPlatform}
                      className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group w-full"
                    >
                      <Star className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      <span>Try Features Now</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Detail Views */}
          {currentView !== 'main' && (
            <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {(() => {
                const feature = getCurrentFeature();
                if (!feature) return null;

                return (
                  <div className="max-w-6xl mx-auto">
                    {/* Back Button */}
                  <button 
                      onClick={handleBackClick}
                      className="mb-8 flex items-center gap-3 glassmorphism px-6 py-3 rounded-xl border border-white/20 hover:bg-gray-50 transition-colors group"
                  >
                      <ArrowLeft className="w-4 h-4 text-[#24c35b] group-hover:-translate-x-1 transition-transform" />
                      <span className="font-medium text-gray-900">Back to Features</span>
                          </button>

                    {/* Feature Header */}
                    <div className="text-center mb-16">
                      <div className="flex items-center justify-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-[#24c35b]/10 rounded-2xl flex items-center justify-center">
                          {feature.icon}
                        </div>
                        <div className="text-left">
                          <h1 className="text-4xl lg:text-5xl font-black text-gray-900">{feature.details.title}</h1>
                          <p className="text-xl text-[#24c35b] font-medium">{feature.details.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                        {feature.details.description}
                      </p>
                        </div>

                    {/* Capabilities Sections */}
                    <div className="space-y-12">
                      {feature.details.capabilities.map((capability, index) => (
                        <div key={index} className="glassmorphism p-8 rounded-2xl border border-white/20">
                          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">{capability.category}</h3>
                          <div className="grid md:grid-cols-2 gap-6">
                            {capability.features.map((featureItem, featureIndex) => (
                              <div key={featureIndex} className="flex items-start gap-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                                <div className="w-2 h-2 bg-[#24c35b] rounded-full mt-2 flex-shrink-0"></div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    {featureItem.split(' - ')[0]}
                                  </h4>
                                  {featureItem.includes(' - ') && (
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                      {featureItem.split(' - ')[1]}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* CTA Section */}
                    <div className="mt-16 text-center glassmorphism p-8 rounded-2xl border border-white/20">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Experience {feature.details.title}?</h3>
                      <p className="text-gray-600 mb-6">Join thousands of traders already using EKKO's advanced features.</p>
                <button 
                        onClick={onLaunchPlatform}
                        className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 inline-flex items-center gap-3 group"
                      >
                        <Target className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        <span>Launch Platform</span>
                </button>
              </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Platform Stats - Bottom */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="glassmorphism px-8 py-4 rounded-xl border border-white/20 flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">20+ Trading Tools</span>
              </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">Sub-100ms Execution</span>
            </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">Enterprise Security</span>
          </div>
        </div>
      </div>
    </div>
  );
} 